import { computeAccessibleName } from '@acsbe/ui';
import { useCallback, useEffect } from 'react';
import { BUTTON_CLICKED } from './event-names';
import { useAnalytics } from './index';

export const AnalyticsGlobalListener = () => {
  const analytics = useAnalytics();
  const mixpanelClick = useMixpanelButtonClick();

  useEffect(() => {
    analytics.mixpanel.init();
  }, [analytics]);

  useEffect(() => {
    const ctrl = new AbortController();
    const { signal } = ctrl;

    function notifyClick({
      buttonName,
      clickSource,
    }: {
      buttonName: string;
      clickSource: HTMLElement;
    }) {
      const sourcedElements = [
        clickSource,
        ...Array.from(clickSource.querySelectorAll('[data-mixpanel]')),
      ] as HTMLElement[];
      const mixpanelSpecificProps = sourcedElements
        .map((el) => el.dataset?.mixpanel?.toString())
        .filter(Boolean)
        .join(',');

      const extraProps = sourcedElements
        .map((el) => {
          if (!el.dataset?.mixpanelProps) return null;
          try {
            return JSON.parse(el.dataset.mixpanelProps);
          } catch {
            return {};
          }
        })
        .filter(Boolean)
        .reduce((acc, cur) => ({ ...acc, ...cur }), {});

      mixpanelClick({
        buttonName,
        buttonTag: mixpanelSpecificProps,
        buttonID: clickSource.id,
        ...extraProps,
      });
    }

    document.addEventListener(
      'mixpanel:click',
      (e) => {
        const src = e.target as HTMLElement;
        const buttonName = computeAccessibleName(src);
        notifyClick({ buttonName, clickSource: src });
      },
      { signal, capture: true, passive: true },
    );

    // Track all clicks on buttons and links
    document.addEventListener(
      'click',
      (e) => {
        const element = e.target as HTMLElement;
        const flags = {
          isModalOverlay: false,
        };
        const isClickable =
          element.role === 'button' ||
          element.tagName === 'BUTTON' ||
          element.tagName === 'A' ||
          element.role === 'link' ||
          element.role === 'tab';

        let clickSource = (
          isClickable
            ? element
            : element.closest(
                "button,[role='button'],a,[role='link'],[role='tab']",
              )
        ) as HTMLElement | null;
        if (!clickSource) {
          const isOverlay = element.nextElementSibling?.matches(
            "dialog, [role='dialog']",
          );
          if (isOverlay) {
            flags.isModalOverlay = true;
            clickSource = element.nextElementSibling! as HTMLElement;
          } else {
            return;
          }
        }

        let buttonName = computeAccessibleName(clickSource);
        if (flags.isModalOverlay) {
          buttonName = `closed`;
        }
        notifyClick({ buttonName, clickSource });
      },
      { signal, capture: true, passive: true },
    );

    return () => {
      ctrl.abort();
    };
  }, [mixpanelClick]);
  return null;
};

export function useMixpanelButtonClick() {
  const analytics = useAnalytics();
  // const user = useStoreData('user');
  // const { data: mainWarning } = trpc.accounts.warning.useQuery(undefined, {
  //   enabled: !!user,
  // });
  // const { domain, type: announcementType } = mainWarning || {
  //   domain: '',
  //   type: '',
  // };

  return useCallback(
    ({
      buttonName,
      buttonID,
      buttonTag,
      ...extraProps
    }: {
      buttonName?: string;
      buttonTag: string;
      buttonID?: string;
      [key: string]: string | number | boolean | undefined;
    }) => {
      analytics.mixpanel.trackEvent(BUTTON_CLICKED, {
        buttonName,
        buttonTag,
        buttonID,
        announcementType: '',
        ...extraProps,
        // announcementDomain: domain,
      });
    },
    [
      analytics.mixpanel,
      // announcementType, domain
    ],
  );
}

export function notifyMixpanelClick(e: HTMLElement) {
  const event = new CustomEvent('mixpanel:click');
  e.dispatchEvent(event);
}
